import api from '../services/api';
import supplierApi from '../services/supplierApi';

/**
 * Get all SLAs
 */
export const getSLAs = async (params = {}) => {
  try {
    const response = await api.get('/sla', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching SLAs:', error);
    throw error;
  }
};

/**
 * Get single SLA by ID
 */
export const getSLAById = async (id) => {
  try {
    const response = await api.get(`/sla/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching SLA:', error);
    throw error;
  }
};

/**
 * Create new SLA
 */
export const createSLA = async (slaData) => {
  try {
    const response = await api.post('/sla', slaData);
    return response.data;
  } catch (error) {
    console.error('Error creating SLA:', error);
    throw error;
  }
};

/**
 * Update existing SLA
 */
export const updateSLA = async (slaId, slaData) => {
  try {
    const response = await api.put(`/sla/${slaId}`, slaData);
    return response.data;
  } catch (error) {
    console.error('Error updating SLA:', error);
    throw error;
  }
};

/**
 * Delete SLA
 */
export const deleteSLA = async (slaId) => {
  try {
    const response = await api.delete(`/sla/${slaId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting SLA:', error);
    throw error;
  }
};

/**
 * Get SLAs for supplier (supplier-side API)
 */
export const getSupplierSLAs = async () => {
  try {
    const response = await supplierApi.get('/supplier/sla');
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier SLAs:', error);
    throw error;
  }
};

/**
 * Get supplier SLA by ID (supplier-side API)
 */
export const getSupplierSLAById = async (slaId) => {
  try {
    const response = await supplierApi.get(`/supplier/sla/${slaId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier SLA:', error);
    throw error;
  }
};

/**
 * Download SLA document
 */
export const downloadSLADocument = async (slaId, isSupplier = false) => {
  try {
    // Use different endpoints for supplier vs admin
    const endpoint = isSupplier ? `/supplier/sla/${slaId}/document` : `/api/sla/${slaId}/document`;
    const apiService = isSupplier ? supplierApi : api;
    
    // Get SLA info first to get the actual filename
    const slaEndpoint = isSupplier ? `/supplier/sla/${slaId}` : `/api/sla/${slaId}`;
    const slaResponse = await apiService.get(slaEndpoint);
    const sla = slaResponse.data.data || slaResponse.data;
    
    // Create download link following helpdesk pattern
    const baseUrl = apiService.defaults.baseURL || window.location.origin;
    const downloadUrl = `${baseUrl}${endpoint}?download=1`;
    
    // Use programmatic download like helpdesk
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = sla.document_name || `SLA_${slaId}.pdf`;
    
    // Add authorization header if needed
    if (isSupplier && supplierApi.defaults.headers.common['Authorization']) {
      // For supplier requests, we need to handle auth differently
      // Create a temporary form to handle the download with auth
      window.open(downloadUrl, '_blank');
    } else {
      // For admin requests, use direct click
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    return { status: 1, message: 'Download started' };
  } catch (error) {
    console.error('Error downloading SLA document:', error);
    throw error;
  }
};

/**
 * Get SLA notifications/alerts
 */
export const getSLANotifications = async () => {
  try {
    const response = await api.get('/sla/notifications');
    return response.data;
  } catch (error) {
    console.error('Error fetching SLA notifications:', error);
    throw error;
  }
};

/**
 * Upload attachment to SLA using standardized tb_attachments table
 */
export const uploadSLAAttachment = async (slaId, formData) => {
  try {
    const response = await api.post(`/api/attachments/sla/${slaId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading SLA attachment:', error);
    return { status: 0, message: error.message };
  }
};

/**
 * Get SLA attachments from tb_attachments table
 */
export const getSLAAttachments = async (slaId) => {
  try {
    const response = await api.get(`/api/attachments/sla/${slaId}/list`);
    return response.data;
  } catch (error) {
    console.error('Error fetching SLA attachments:', error);
    return { status: 0, message: error.message, attachments: [] };
  }
};

/**
 * Download SLA attachment using standardized system
 */
export const downloadSLAAttachment = async (slaId) => {
  try {
    // Get attachments first to find the file to download
    const attachmentsResponse = await getSLAAttachments(slaId);
    if (attachmentsResponse.status === 1 && attachmentsResponse.attachments.length > 0) {
      const attachment = attachmentsResponse.attachments[0]; // Get first attachment
      
      // Create download link using the standardized attachment download endpoint
      const link = document.createElement('a');
      link.href = `/api/attachments/download/${attachment.attachment_id}?download=1`;
      link.download = attachment.original_filename || attachment.filename;
      
      // Add authorization header if needed
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      return { status: 1, message: 'Download started' };
    } else {
      return { status: 0, message: 'No attachments found' };
    }
  } catch (error) {
    console.error('Error downloading SLA attachment:', error);
    return { status: 0, message: error.message };
  }
};